import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useCoachMode } from "./CoachModeContext";
import { fetchCoachTeam } from "./coachServices";
import { activeTeamOf, leagueLabelOf, teamKey, teamNameOf } from "./coachUtils";
import { CoachPhoneHead, LockedCard, SavedRosterNotice, SectionActions, TeamSwitch } from "./coachPieces";
import { TEAM_TABS, rosterSubtitle } from "./team/teamModel";
import useTeamStyle from "./team/useTeamStyle";
import useTeamPerformance from "./team/useTeamPerformance";
import TeamKpiStrip from "./team/TeamKpiStrip";
import TeamSubTabs from "./team/TeamSubTabs";
import TeamRosterTab from "./team/TeamRosterTab";
import TeamPerformanceTab from "./team/TeamPerformanceTab";
import TeamNilTab from "./team/TeamNilTab";
import RosterModals from "./RosterModals";
import { coachVerifyAthlete } from "../../auth/Onboarding/services/onboardingServices";
import { ErrorState, Loading, PageHeader, SegmentedControl, Select } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import { SegmentedControl as PhoneSegmented } from "../../auth/v2/pieces";

// Coach Mode · Team (Figma 02 · Team Roster, 02b · Team Performance, 02c ·
// Team NIL Intelligence): the header with the team switch and roster
// actions, the KPI strip, then one of three views. The team style and the
// game analytics of the Performance view (coach/team/style/ and
// coach/team/performance/) load on their own the first time that view
// opens, for the team and season of the roster on screen.
//
// On phones (Figma COACH MODE V2 mobile, 02 · Team 2407:793, 02b 2409:907,
// 02c 2411:1193) the sub tabs sit above the page head, sticky under the
// top bar and bled to the screen edges (D-c2); then the phone head (no
// eyebrow, the 56px team switch, Manage roster and CacheShare sharing a
// 52px row, the full-width Team Code, then the league-admin controls),
// and one column 18px apart. Each card picks its phone layout itself. The
// page keeps one tree shape at every width, so the open tab's cards and
// the roster modals keep their state when a phone turns to landscape.

const BRAND_MATCH_REFRESH_MS = 30000;

// The squad whose roster is on screen, {teamId, league}: the team the server
// answered for (its selector), else the team the page asked for. Verify
// sends it, so an athlete is verified on that squad and never moved to
// another age group of the program (10/19 notes, M-CODES 1.3c).
export const rosterTeamOf = (data, asked) => {
  const sel = data?.selector;
  const team = sel?.teamId ? sel : asked;
  return team?.teamId ? { teamId: String(team.teamId), league: team.league || "" } : null;
};

// Why Verify failed: the server's sentence for a refusal (a 4xx), else a
// plain line. A 5xx body's text is never shown.
const verifyRefusal = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const words = status && status < 500 && data && typeof data === "object" ? data.message || data.error : "";
  return (typeof words === "string" && words.trim()) || "Could not verify this player.";
};

const VIEW_OPTIONS = [
  { value: "coach", label: "Coach view" },
  { value: "league", label: "League admin view" },
];

const SeasonOptions = ({ seasons }) =>
  seasons.map((label, i) => (
    <option key={label} value={label}>
      {label}
      {i === 0 ? " (current)" : ""}
    </option>
  ));

// League-admin view and past seasons: only shown when there is a choice.
// Phones (not drawn): full width under the header actions, the mobile
// segmented and a 56px season select, as on CacheShare (D-cs8).
const TeamScopeControls = ({ selection, onView, onSeason, phone = false }) => {
  const seasons = selection.seasons || [];
  if (!selection.leagueAdmin && seasons.length < 2) return null;
  if (phone) {
    return (
      <>
        {selection.leagueAdmin && <PhoneSegmented label="View" value={selection.view} onChange={onView} options={VIEW_OPTIONS.map((o) => [o.value, o.label])} />}
        {seasons.length > 1 && (
          <Select value={selection.season} onChange={(e) => onSeason(e.target.value)} className="bm-input-lg" aria-label="Select season">
            <SeasonOptions seasons={seasons} />
          </Select>
        )}
      </>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      {selection.leagueAdmin && (
        <SegmentedControl value={selection.view} onChange={onView} options={VIEW_OPTIONS} />
      )}
      {seasons.length > 1 && (
        <Select value={selection.season} onChange={(e) => onSeason(e.target.value)} className="!w-auto" aria-label="Select season">
          <SeasonOptions seasons={seasons} />
        </Select>
      )}
    </div>
  );
};

const CoachTeamPage = () => {
  const navigate = useNavigate();
  const phone = usePhoneTheme();
  const [params, setParams] = useSearchParams();
  const { selection, absorbSelector, selectTeam, selectSeason, switchView, refreshBadges } = useCoachMode();
  const [data, setData] = useState(null);
  // The team and season (as the server resolved it) of the roster on
  // screen. The Performance style follows this, not the selection: a team
  // switch clears the season until the roster answers, so keying on the
  // selection would ask for the style twice (no season, then the resolved
  // one). A first load without a team leaves it unset, and the style waits
  // for the reload that names the team.
  const [shown, setShown] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [modal, setModal] = useState(null); // "code" | "roster"
  const tab = TEAM_TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "roster";
  const filter = params.get("filter") || "all";
  const setView = ({ tab: nextTab = tab, filter: nextFilter = filter }) =>
    setParams({ tab: nextTab, ...(nextFilter !== "all" ? { filter: nextFilter } : {}) });
  // A phone tab switch starts the new view from the top (the tab bar stays
  // pinned, so a long roster would otherwise leave the next tab half
  // scrolled), as the athlete profile tabs do.
  const selectTab = (next) => {
    setView({ tab: next });
    if (phone && next !== tab && typeof window.scrollTo === "function") window.scrollTo(0, 0);
  };

  const load = async (team, season, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const payload = await fetchCoachTeam(team, season);
      setData(payload);
      if (team) setShown({ team, season: payload.selector?.season || "" });
      absorbSelector(payload.selector, team);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your team");
    } finally {
      setLoading(false);
    }
  };
  const reload = (options) => load(selection.team, selection.season, options);

  // Keyed on team AND league: one program can be several squads that share
  // a team id (17U / 16U / 15U), one per league.
  const selectedTeam = teamKey(selection.team);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, selection.season]);

  // While the server computes missing brand matches, quietly refetch so the
  // brand categories fill themselves in.
  useEffect(() => {
    if (!data?.nil?.matchesComputing) return undefined;
    const timer = setTimeout(() => reload({ silent: true }), BRAND_MATCH_REFRESH_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const accountsVisible = data?.selector?.accountsVisible !== false;
  const performanceOpen = tab === "performance" && accountsVisible && !!selection.team;
  const teamStyle = useTeamStyle({ team: shown?.team, season: shown?.season, enabled: performanceOpen });
  const teamPerf = useTeamPerformance({ team: shown?.team, season: shown?.season, enabled: performanceOpen });
  const openProfile = (athleteId) => navigate(`/business/athlette/profile/${athleteId}`);

  const verify = async (p) => {
    setVerifyingId(p.athleteId);
    try {
      await coachVerifyAthlete(p.athleteId, rosterTeamOf(data, shown?.team || activeTeamOf(selection)));
      setData((d) => d && { ...d, players: d.players.map((x) => (x.athleteId === p.athleteId ? { ...x, verified: true } : x)) });
      toast.success(`${p.name} ${p.surname} verified.`);
      refreshBadges();
    } catch (err) {
      toast.error(verifyRefusal(err));
    } finally {
      setVerifyingId(null);
    }
  };

  if (error && !data) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { team, summary, performance, nil } = data;
  const players = data.players || [];
  const totals = team?.totals || {};
  const activeTeam = activeTeamOf(selection);

  const teamSwitch = <TeamSwitch selection={selection} onSelect={selectTeam} disabled={loading} />;
  const sectionActions = (
    <SectionActions
      onManage={activeTeam ? () => setModal("roster") : null}
      onCacheShare={() => navigate("/coach/cacheshare")}
      onTeamCode={() => setModal("code")}
    />
  );
  // The team by the one display rule ("Oakland Soldiers 16U") under its
  // league, which keeps no season and does not say the age group again.
  const title = teamNameOf(team) || "Your team";
  const league = leagueLabelOf(team?.league, title);
  const subtitle = rosterSubtitle(totals);

  return (
    <div className={phone ? "flex flex-col" : undefined}>
      {phone ? (
        accountsVisible && <TeamSubTabs value={tab} onChange={selectTab} className="-mx-6 -mt-5" />
      ) : (
        <PageHeader
          align="center"
          eyebrow={[league, "Coach Mode"].filter(Boolean).join(" · ")}
          title={title}
          subtitle={subtitle}
          actions={
            <div className="flex flex-wrap items-center gap-6">
              {teamSwitch}
              {sectionActions}
            </div>
          }
        />
      )}
      {!phone && <TeamScopeControls selection={selection} onView={switchView} onSeason={selectSeason} />}

      <div className={phone ? `flex flex-col gap-[18px] ${accountsVisible ? "pt-5" : ""}`.trim() : "flex flex-col gap-4 pt-7"}>
        {phone && (
          <CoachPhoneHead title={title} sub={subtitle} switcher={teamSwitch}>
            {sectionActions}
            <TeamScopeControls selection={selection} onView={switchView} onSeason={selectSeason} phone />
          </CoachPhoneHead>
        )}
        {error && <ErrorState>{error}</ErrorState>}
        {team?.rosterSnapshot && <SavedRosterNotice asOf={team.rosterSnapshot.asOf} />}
        {accountsVisible ? (
          <>
            <TeamKpiStrip summary={summary} totals={totals} />
            {!phone && <TeamSubTabs value={tab} onChange={selectTab} />}
            {tab === "roster" && (
              <TeamRosterTab
                players={players}
                filter={filter}
                onFilter={(next) => setView({ filter: next })}
                onOpen={(p) => openProfile(p.athleteId)}
                onVerify={verify}
                verifyingId={verifyingId}
              />
            )}
            {tab === "performance" && (
              <TeamPerformanceTab
                players={players}
                performance={performance}
                style={teamStyle.style}
                styleLoading={teamStyle.loading}
                perf={teamPerf.perf}
                perfLoading={teamPerf.loading}
                perfBuilding={teamPerf.building}
                onRetryPerf={teamPerf.retry}
                onOpenProfile={openProfile}
              />
            )}
            {tab === "nil" && <TeamNilTab players={players} summary={summary} totals={totals} nil={nil} performance={performance} />}
          </>
        ) : (
          <LockedCard title="Team analytics locked">
            Your roster's scores, values and NIL numbers open once CACHE verifies you as this team's coach.
          </LockedCard>
        )}
      </div>

      <RosterModals modal={modal} onClose={() => setModal(null)} team={activeTeam} players={players} onChanged={() => reload({ silent: true })} />
    </div>
  );
};

export default CoachTeamPage;
